/**
 * useTokenPrices Hook
 *
 * Fetches Jupiter price quotes for tokens on-demand to determine
 * which tokens are eligible for dust swapping (value >= 0.002 SOL).
 */

import { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getJupiterPriceQuotes,
  isDustSwapEnabled,
  type TokenPriceQuote,
  type SwappableToken,
} from '@/lib/solana/jupiter';
import { DUST_SWAP_CONSTANTS } from '@/lib/constants';

/**
 * Extended price data including UI-specific fields
 */
export interface TokenPriceData extends TokenPriceQuote {
  /** Formatted display value (e.g., "0.05 SOL") */
  displayValue: string;
  /** Net value after 10% platform fee */
  netSolValue: number;
}

/**
 * Return type for useTokenPrices hook
 */
export interface UseTokenPricesReturn {
  /** Map of mint address to price data */
  prices: Map<string, TokenPriceData>;
  /** Whether prices are currently being fetched */
  isLoading: boolean;
  /** Whether there was an error fetching prices */
  isError: boolean;
  /** Error message if any */
  error: Error | null;
  /** Fetch prices for given tokens (on-demand) */
  fetchPrices: (tokens: SwappableToken[]) => Promise<void>;
  /** Refresh prices (uses cached tokens) */
  refreshPrices: () => Promise<void>;
  /** Get list of swappable tokens from prices (meets minimum value threshold) */
  getSwappableTokens: () => TokenPriceData[];
  /** Get list of tokens that have swap routes (regardless of value) */
  getTokensWithRoutes: () => TokenPriceData[];
  /** Get total estimated SOL value of all swappable tokens */
  getTotalSwapValue: () => number;
  /** Get total net value after fees */
  getTotalNetValue: () => number;
  /** Check if a specific token is swappable (has route AND meets minimum) */
  isTokenSwappable: (mint: string) => boolean;
  /** Check if a specific token has a swap route (regardless of value) */
  hasTokenSwapRoute: (mint: string) => boolean;
  /** Check if a specific token meets the minimum value threshold */
  tokenMeetsMinimum: (mint: string) => boolean;
  /** Get price data for a specific token */
  getTokenPrice: (mint: string) => TokenPriceData | undefined;
}

/**
 * Format SOL value for display
 */
function formatSolValue(value: number): string {
  if (value === 0) return '0 SOL';
  if (value < 0.0001) return '<0.0001 SOL';
  if (value < 0.01) return `${value.toFixed(4)} SOL`;
  return `${value.toFixed(3)} SOL`;
}

/**
 * Calculate net value after platform fee
 */
function calculateNetValue(grossValue: number): number {
  const feeMultiplier = 1 - DUST_SWAP_CONSTANTS.PLATFORM_FEE_PERCENT / 100;
  return grossValue * feeMultiplier;
}

/**
 * Hook for fetching and managing token price quotes from Jupiter
 *
 * @example
 * ```tsx
 * const { prices, isLoading, fetchPrices, getSwappableTokens } = useTokenPrices();
 *
 * // Fetch prices when needed
 * useEffect(() => {
 *   if (tokens.length > 0 && shouldFetchPrices) {
 *     fetchPrices(tokens);
 *   }
 * }, [tokens, shouldFetchPrices]);
 *
 * // Get swappable tokens
 * const swappable = getSwappableTokens();
 * ```
 */
export function useTokenPrices(): UseTokenPricesReturn {
  const queryClient = useQueryClient();
  const [tokensToFetch, setTokensToFetch] = useState<SwappableToken[]>([]);

  // Query for fetching prices
  const {
    data: priceQuotes,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tokenPrices', tokensToFetch.map(t => t.mint).sort().join(',')],
    queryFn: async () => {
      console.log('[useTokenPrices] queryFn called');
      console.log('[useTokenPrices] isDustSwapEnabled():', isDustSwapEnabled());
      console.log('[useTokenPrices] tokensToFetch.length:', tokensToFetch.length);

      if (!isDustSwapEnabled()) {
        console.log('[useTokenPrices] Dust swap disabled, skipping price fetch');
        return [];
      }

      if (tokensToFetch.length === 0) {
        console.log('[useTokenPrices] No tokens to fetch, returning empty array');
        return [];
      }

      console.log(`[useTokenPrices] Fetching prices for ${tokensToFetch.length} tokens:`,
        tokensToFetch.map(t => `${t.symbol}(${t.mint.slice(0,8)}...)`));

      try {
        const result = await getJupiterPriceQuotes(tokensToFetch);
        console.log('[useTokenPrices] Price quotes received:', result.length);
        console.log('[useTokenPrices] Swappable tokens:', result.filter(r => r.isSwappable).map(r => r.symbol));
        console.log('[useTokenPrices] Non-swappable tokens:', result.filter(r => !r.isSwappable).map(r => `${r.symbol}: ${r.error || 'below threshold'}`));
        return result;
      } catch (err: any) {
        console.error('[useTokenPrices] Error fetching price quotes:', err);
        console.error('[useTokenPrices] Error details:', {
          name: err?.name,
          message: err?.message,
          stack: err?.stack,
        });
        throw err;
      }
    },
    enabled: tokensToFetch.length > 0 && isDustSwapEnabled(),
    staleTime: 60_000, // 1 minute cache
    gcTime: 5 * 60_000, // 5 minute garbage collection
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Transform price quotes into Map with extended data
  const prices = useMemo(() => {
    const priceMap = new Map<string, TokenPriceData>();

    if (!priceQuotes) return priceMap;

    for (const quote of priceQuotes) {
      const netSolValue = calculateNetValue(quote.estimatedSolValue);
      priceMap.set(quote.mint, {
        ...quote,
        displayValue: formatSolValue(quote.estimatedSolValue),
        netSolValue,
      });
    }

    return priceMap;
  }, [priceQuotes]);

  // Fetch prices for given tokens
  const fetchPrices = useCallback(async (tokens: SwappableToken[]) => {
    if (!isDustSwapEnabled()) {
      console.log('[useTokenPrices] Dust swap disabled');
      return;
    }

    if (tokens.length === 0) {
      console.log('[useTokenPrices] No tokens to fetch');
      return;
    }

    // Filter out tokens we already have cached
    const cachedKey = tokensToFetch.map(t => t.mint).sort().join(',');
    const newKey = tokens.map(t => t.mint).sort().join(',');

    if (cachedKey === newKey && prices.size > 0) {
      console.log('[useTokenPrices] Using cached prices');
      return;
    }

    setTokensToFetch(tokens);
  }, [tokensToFetch, prices.size]);

  // Refresh prices (re-fetch for current tokens)
  const refreshPrices = useCallback(async () => {
    if (tokensToFetch.length > 0) {
      // Invalidate cache and refetch
      await queryClient.invalidateQueries({
        queryKey: ['tokenPrices'],
      });
      await refetch();
    }
  }, [tokensToFetch, queryClient, refetch]);

  // Get list of swappable tokens (has route AND meets minimum)
  const getSwappableTokens = useCallback((): TokenPriceData[] => {
    return Array.from(prices.values()).filter(
      p => p.hasSwapRoute && p.meetsMinimum
    );
  }, [prices]);

  // Get list of tokens that have swap routes (regardless of value)
  const getTokensWithRoutes = useCallback((): TokenPriceData[] => {
    return Array.from(prices.values()).filter(p => p.hasSwapRoute);
  }, [prices]);

  // Get total estimated SOL value
  const getTotalSwapValue = useCallback((): number => {
    return getSwappableTokens().reduce((sum, p) => sum + p.estimatedSolValue, 0);
  }, [getSwappableTokens]);

  // Get total net value after fees
  const getTotalNetValue = useCallback((): number => {
    return getSwappableTokens().reduce((sum, p) => sum + p.netSolValue, 0);
  }, [getSwappableTokens]);

  // Check if a specific token is swappable (has route AND meets minimum)
  const isTokenSwappable = useCallback(
    (mint: string): boolean => {
      const priceData = prices.get(mint);
      return priceData?.hasSwapRoute === true && priceData?.meetsMinimum === true;
    },
    [prices]
  );

  // Check if a specific token has a swap route (regardless of value)
  const hasTokenSwapRoute = useCallback(
    (mint: string): boolean => {
      const priceData = prices.get(mint);
      return priceData?.hasSwapRoute === true;
    },
    [prices]
  );

  // Check if a specific token meets the minimum value threshold
  const tokenMeetsMinimum = useCallback(
    (mint: string): boolean => {
      const priceData = prices.get(mint);
      return priceData?.meetsMinimum === true;
    },
    [prices]
  );

  // Get price data for a specific token
  const getTokenPrice = useCallback(
    (mint: string): TokenPriceData | undefined => {
      return prices.get(mint);
    },
    [prices]
  );

  return {
    prices,
    isLoading,
    isError,
    error: error as Error | null,
    fetchPrices,
    refreshPrices,
    getSwappableTokens,
    getTokensWithRoutes,
    getTotalSwapValue,
    getTotalNetValue,
    isTokenSwappable,
    hasTokenSwapRoute,
    tokenMeetsMinimum,
    getTokenPrice,
  };
}
